import React from 'react';
import { Server, Wifi, WifiOff, Clock } from 'lucide-react';
import { PrinterDto, AgentInfo } from '../../../services/api';
import { PrinterCard } from './PrinterCard';

interface AgentGroupViewProps {
  printers: PrinterDto[];
  onViewQueue: (printerId: string, printerName: string) => void;
}

interface AgentGroup {
  agent: AgentInfo;
  printers: PrinterDto[];
}

export const AgentGroupView: React.FC<AgentGroupViewProps> = ({ printers, onViewQueue }) => {
  // Group printers by agent
  const groups = React.useMemo(() => {
    const agentMap = new Map<string, AgentGroup>();
    
    // Collecting unique agents and their printers
    // Note: A printer can be managed by multiple agents, so it might appear in multiple groups
    // If we want each printer to appear only once, we'd need a different strategy,
    // but typically "Group by Agent" implies showing the agent's responsibilities.
    
    printers.forEach(printer => {
      if (printer.agents && printer.agents.length > 0) {
        printer.agents.forEach(agent => {
          if (!agentMap.has(agent.agentId)) {
            agentMap.set(agent.agentId, {
              agent,
              printers: []
            });
          }
          agentMap.get(agent.agentId)!.printers.push(printer);
        });
      } else {
        // Handle printers with no agents if necessary, or put them in an "Unassigned" group
        const unassignedId = 'unassigned';
        if (!agentMap.has(unassignedId)) {
          agentMap.set(unassignedId, {
            agent: {
              agentId: 'unassigned',
              agentHostname: 'Sin Agente Asignado',
              status: 'unknown',
              isOnline: false,
              lastSeen: new Date().toISOString()
            },
            printers: []
          });
        }
        agentMap.get(unassignedId)!.printers.push(printer);
      }
    });

    return Array.from(agentMap.values()).sort((a, b) => {
      // Sort online agents first
      if (a.agent.isOnline !== b.agent.isOnline) return a.agent.isOnline ? -1 : 1;
      return a.agent.agentHostname.localeCompare(b.agent.agentHostname);
    });
  }, [printers]);

  const formatLastSeen = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'Ahora';
      if (diffMins < 60) return `${diffMins}m`;
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h`;
      return `${Math.floor(diffHours / 24)}d`;
    } catch {
      return '-';
    }
  };

  if (groups.length === 0) {
    return (
      <div className="text-center py-12 text-slate-400">
        <Server size={48} className="mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">No se encontraron agentes</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {groups.map((group) => (
        <div key={group.agent.agentId} className="bg-slate-50/50 rounded-3xl p-4 md:p-6 border border-slate-200">
          {/* Agent Header */}
          <div className="flex items-center justify-between mb-6 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                group.agent.isOnline ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
              }`}>
                <Server size={24} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">
                  {group.agent.agentHostname}
                </h3>
                <div className="flex items-center gap-3 text-sm">
                  <span className={`inline-flex items-center gap-1.5 font-medium ${
                    group.agent.isOnline ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {group.agent.isOnline ? <Wifi size={14} /> : <WifiOff size={14} />}
                    {group.agent.isOnline ? 'Online' : 'Offline'}
                  </span>
                  <span className="text-slate-400">•</span>
                  <span className="flex items-center gap-1 text-slate-500">
                    <Clock size={14} />
                    {formatLastSeen(group.agent.lastSeen)}
                  </span>
                </div>
              </div>
            </div>
            <div className="hidden sm:block text-right">
              <span className="block text-2xl font-black text-slate-800 leading-none">
                {group.printers.length}
              </span>
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Impresoras
              </span>
            </div>
          </div>

          {/* Printers Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {group.printers.map(printer => (
              <PrinterCard 
                key={printer.id} 
                printer={printer} 
                onViewQueue={onViewQueue}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
